import React, { useMemo, useState } from 'react';
import { Row } from 'antd';
import { SelectValue } from 'antd/lib/select';

import {
  ELMA365AppElementToolFeedLoadMode,
  ELMA365GeneralToolsetSettingsInput,
  LLMSettings,
  ProviderModel,
  ToolsetModel,
} from '../../../../../../gamma-api';
import IbInput from '../../../../../components/common/IbInput';
import IbTypography from '../../../../../components/common/IbTypography';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import IbIcon from '../../../../../components/common/IbIcon';
import ConnectorBlock from '../../../../../components/ConnectorBlock';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import { tryParseIntValue } from '../../../../../utils/numberUtil';
import IbSelect from '../../../../../components/common/IbSelect';
import ModelSelector from '../../../../../components/ModelSelector';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbTooltip from '../../../../../components/common/IbTooltip';
import IbTabs from '../../../../../components/common/IbTabs';
import IbTabPane from '../../../../../components/common/IbTabPane';
import IbBadge from '../../../../../components/common/IbBadge';
import PromptEditorExpandable from '../../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../../components/common/CodeEditorExpandable/types';

import {
  ELMA365_TOOL_NAME_EXAMPLE,
  FORM_ITEM_CLASS_NAME,
  PARAMETERS_CLASS_NAME,
  SECTION_CLASS_NAME,
  SECTION_FORM_CLASS_NAME,
  SECTION_FORM_ITEM_CLASS_NAME,
  SECTION_TITLE_CLASS_NAME,
  TABS_CLASS_NAME,
  TOOL_NAMES_PREFIX_HINT_CLASS_NAME,
} from './const';

enum Elma365GeneralParametersTabKey {
  Applications = 'Applications',
  Files = 'Files',
  Users = 'Users',
  Tasks = 'Tasks',
  Docflow = 'Docflow',
}

interface IElma365GeneralParametersProps {
  editToolset: ToolsetModel;
  providers: ProviderModel[];
  providersLoading: boolean;
  onChange: (toolset: ToolsetModel) => void;
}

const Elma365GeneralParameters: React.FC<IElma365GeneralParametersProps> = ({
  editToolset,
  providers,
  providersLoading,
  onChange,
}) => {
  const translate = getTranslateFunc('Agent: Toolset: Modal: Settings:');
  const elma365ToolsetSettings = editToolset.settings as ELMA365GeneralToolsetSettingsInput;
  const [tabKey, setTabKey] = useState(Elma365GeneralParametersTabKey.Applications);

  const appToolCount = useMemo(
    () =>
      [
        elma365ToolsetSettings.fetch_app_element_tool_settings.enabled,
        elma365ToolsetSettings.fetch_app_feed_tool_settings.enabled,
      ].filter(Boolean).length,
    [elma365ToolsetSettings]
  );

  const fileToolCount = useMemo(
    () =>
      [
        elma365ToolsetSettings.read_file_tool_settings.enabled,
        elma365ToolsetSettings.describe_image_tool_settings.enabled,
      ].filter(Boolean).length,
    [elma365ToolsetSettings]
  );

  const userToolCount = useMemo(() => (elma365ToolsetSettings.fetch_current_user_tool_settings.enabled ? 1 : 0), [
    elma365ToolsetSettings,
  ]);

  const taskToolCount = useMemo(
    () =>
      [
        elma365ToolsetSettings.search_elma365_tasks_tool_settings.enabled,
        elma365ToolsetSettings.fetch_elma365_task_tool_settings.enabled,
        elma365ToolsetSettings.create_elma365_task_tool_settings.enabled,
        elma365ToolsetSettings.reassign_elma365_task_tool_settings.enabled,
        elma365ToolsetSettings.update_elma365_task_percent_tool_settings.enabled,
        elma365ToolsetSettings.update_elma365_task_dates_tool_settings.enabled,
      ].filter(Boolean).length,
    [elma365ToolsetSettings]
  );

  const docflowToolCount = useMemo(() => (elma365ToolsetSettings.fetch_docflow_lists_tool_settings.enabled ? 1 : 0), [
    elma365ToolsetSettings,
  ]);

  const onTabChange = (activeKey: string) => {
    setTabKey(activeKey as Elma365GeneralParametersTabKey);
  };

  const onConnectorChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        connector_id: value,
      },
    } as ToolsetModel);
  };

  const onToolNamesPrefixChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        tool_names_prefix: value,
      },
    } as ToolsetModel);
  };

  const onFetchAppElementEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_app_element_tool_settings: {
          ...elma365ToolsetSettings.fetch_app_element_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onFetchAppFeedEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_app_feed_tool_settings: {
          ...elma365ToolsetSettings.fetch_app_feed_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onReadFileEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        read_file_tool_settings: {
          ...elma365ToolsetSettings.read_file_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onDescribeImageEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...elma365ToolsetSettings.describe_image_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onFeedLoadModeChange = (value: SelectValue) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_app_element_tool_settings: {
          ...elma365ToolsetSettings.fetch_app_element_tool_settings,
          feed_load_mode: value,
        },
      },
    } as ToolsetModel);
  };

  const onFeedMaxContentTokenCountChange = (value: string | number) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_app_feed_tool_settings: {
          ...elma365ToolsetSettings.fetch_app_feed_tool_settings,
          max_content_token_count: tryParseIntValue(value) ?? 0,
        },
      },
    } as ToolsetModel);
  };

  const onMaxContentTokenCountChange = (value: string | number) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        read_file_tool_settings: {
          ...elma365ToolsetSettings.read_file_tool_settings,
          max_content_token_count: tryParseIntValue(value) ?? 0,
        },
      },
    } as ToolsetModel);
  };

  const onLLMChange = (value: LLMSettings) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...elma365ToolsetSettings.describe_image_tool_settings,
          llm_settings: value,
        },
      },
    } as ToolsetModel);
  };

  const onImageDescribePromptChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...elma365ToolsetSettings.describe_image_tool_settings,
          default_prompt: value,
        },
      },
    } as ToolsetModel);
  };

  const onAllowPromptOverrideChange = (value: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...elma365ToolsetSettings.describe_image_tool_settings,
          allow_prompt_override: value,
        },
      },
    } as ToolsetModel);
  };

  const onFetchCurrentUserEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_current_user_tool_settings: {
          ...elma365ToolsetSettings.fetch_current_user_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onSearchTasksEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        search_elma365_tasks_tool_settings: {
          ...elma365ToolsetSettings.search_elma365_tasks_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onFetchTaskEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_elma365_task_tool_settings: {
          ...elma365ToolsetSettings.fetch_elma365_task_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onCreateTaskEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        create_elma365_task_tool_settings: {
          ...elma365ToolsetSettings.create_elma365_task_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onReassignTaskEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        reassign_elma365_task_tool_settings: {
          ...elma365ToolsetSettings.reassign_elma365_task_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onUpdateTaskPercentEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        update_elma365_task_percent_tool_settings: {
          ...elma365ToolsetSettings.update_elma365_task_percent_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onUpdateTaskDatesEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        update_elma365_task_dates_tool_settings: {
          ...elma365ToolsetSettings.update_elma365_task_dates_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onFetchDocflowListsEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        fetch_docflow_lists_tool_settings: {
          ...elma365ToolsetSettings.fetch_docflow_lists_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const renderConnectorSettings = () => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {translate('Connector: Title')}
      </IbTypography.Paragraph>
      <ConnectorBlock connectorId={elma365ToolsetSettings.connector_id} onChange={onConnectorChange} />
    </div>
  );

  const renderToolNamesPrefixSettings = () => (
    <div className={FORM_ITEM_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {translate('Tool names prefix: Title')}
      </IbTypography.Paragraph>
      <IbInput value={elma365ToolsetSettings.tool_names_prefix} onChange={onToolNamesPrefixChange} />
      {elma365ToolsetSettings.tool_names_prefix && (
        <div className={TOOL_NAMES_PREFIX_HINT_CLASS_NAME}>
          <IbTypography.Paragraph disabled strong type="secondary">
            {translate('Tool names prefix: Example')}
          </IbTypography.Paragraph>
          <IbTooltip title={`${elma365ToolsetSettings.tool_names_prefix}${ELMA365_TOOL_NAME_EXAMPLE}`}>
            <IbTypography.Paragraph ellipsis type="secondary">
              {`${elma365ToolsetSettings.tool_names_prefix}${ELMA365_TOOL_NAME_EXAMPLE}`}
            </IbTypography.Paragraph>
          </IbTooltip>
        </div>
      )}
    </div>
  );

  const renderAppTools = () => (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.fetch_app_element_tool_settings.enabled}
            size="small"
            onChange={onFetchAppElementEnabledChange}
          />
          <IbIcon iconName="ad-product" size={20} />
          <IbTypography.Paragraph strong>{translate('ELMA365: Fetch app element tool: Title')}</IbTypography.Paragraph>
        </div>
        {elma365ToolsetSettings.fetch_app_element_tool_settings.enabled && (
          <div className={SECTION_FORM_CLASS_NAME}>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('ELMA365: Fetch app element tool: Feed load mode: Title')}
              </IbTypography.Paragraph>
              <IbSelect
                options={[
                  {
                    value: ELMA365AppElementToolFeedLoadMode.MetadataOnly,
                    label: translate('ELMA365: Fetch app element tool: Feed load mode: Metadata only'),
                  },
                  {
                    value: ELMA365AppElementToolFeedLoadMode.AllData,
                    label: translate('ELMA365: Fetch app element tool: Feed load mode: All data'),
                  },
                  {
                    value: ELMA365AppElementToolFeedLoadMode.DecisionByLlm,
                    label: translate('ELMA365: Fetch app element tool: Feed load mode: Decision by LLM'),
                  },
                ]}
                value={elma365ToolsetSettings.fetch_app_element_tool_settings.feed_load_mode}
                onChange={onFeedLoadModeChange}
              />
            </div>
          </div>
        )}
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.fetch_app_feed_tool_settings.enabled}
            size="small"
            onChange={onFetchAppFeedEnabledChange}
          />
          <IbIcon iconName="comments" size={20} />
          <IbTypography.Paragraph strong>{translate('ELMA365: Fetch app feed tool: Title')}</IbTypography.Paragraph>
        </div>
        {elma365ToolsetSettings.fetch_app_feed_tool_settings.enabled && (
          <div className={SECTION_FORM_CLASS_NAME}>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('ELMA365: Fetch app feed tool: Max content length: Title')}
              </IbTypography.Paragraph>
              <IbNumberInput
                min={0}
                precision={0}
                value={elma365ToolsetSettings.fetch_app_feed_tool_settings.max_content_token_count}
                onChange={onFeedMaxContentTokenCountChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderFileTools = () => (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.read_file_tool_settings.enabled}
            size="small"
            onChange={onReadFileEnabledChange}
          />
          <IbIcon iconName="file-text" size={20} />
          <IbTypography.Paragraph strong>{translate('ELMA365: Read file tool: Title')}</IbTypography.Paragraph>
        </div>
        {elma365ToolsetSettings.read_file_tool_settings.enabled && (
          <div className={SECTION_FORM_CLASS_NAME}>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('ELMA365: Read file tool: Max content length: Title')}
              </IbTypography.Paragraph>
              <IbNumberInput
                min={0}
                precision={0}
                value={elma365ToolsetSettings.read_file_tool_settings.max_content_token_count}
                onChange={onMaxContentTokenCountChange}
              />
            </div>
          </div>
        )}
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.describe_image_tool_settings.enabled}
            size="small"
            onChange={onDescribeImageEnabledChange}
          />
          <IbIcon iconName="image-files" size={20} />
          <IbTypography.Paragraph strong>{translate('ELMA365: Describe image tool: Title')}</IbTypography.Paragraph>
        </div>
        {elma365ToolsetSettings.describe_image_tool_settings.enabled && (
          <div className={SECTION_FORM_CLASS_NAME}>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('ELMA365: Describe image tool: LLM: Title')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                {translate('ELMA365: Describe image tool: LLM: Description')}
              </IbTypography.Paragraph>
              <ModelSelector.LLM
                loading={providersLoading}
                providers={providers}
                settings={elma365ToolsetSettings.describe_image_tool_settings.llm_settings}
                onChange={onLLMChange}
              />
            </div>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('ELMA365: Describe image tool: Prompt: Title')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                {translate('ELMA365: Describe image tool: Prompt: Description')}
              </IbTypography.Paragraph>
              <PromptEditorExpandable
                promptLanguage={PromptLanguage.PLAIN_TEXT}
                size={CodeEditorSize.MD}
                value={elma365ToolsetSettings.describe_image_tool_settings.default_prompt}
                onChange={onImageDescribePromptChange}
              />
            </div>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <Row>
                <IbSwitch
                  checked={elma365ToolsetSettings.describe_image_tool_settings.allow_prompt_override}
                  size="small"
                  onChange={onAllowPromptOverrideChange}
                />
                <IbTypography.Paragraph strong type="secondary">
                  {translate('ELMA365: Describe image tool: Allow prompt override: Title')}
                </IbTypography.Paragraph>
              </Row>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderUserTools = () => (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.fetch_current_user_tool_settings.enabled}
            size="small"
            onChange={onFetchCurrentUserEnabledChange}
          />
          <IbIcon iconName="user" size={20} />
          <IbTypography.Paragraph strong>{translate('ELMA365: Fetch current user tool: Title')}</IbTypography.Paragraph>
        </div>
      </div>
    </div>
  );

  const renderTaskTools = () => (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.search_elma365_tasks_tool_settings.enabled}
            size="small"
            onChange={onSearchTasksEnabledChange}
          />
          <IbIcon iconName="clipboard" size={20} />
          <IbTypography.Paragraph strong>
            {translate('ELMA365: Search ELMA365 tasks tool: Title')}
          </IbTypography.Paragraph>
        </div>
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.fetch_elma365_task_tool_settings.enabled}
            size="small"
            onChange={onFetchTaskEnabledChange}
          />
          <IbIcon iconName="clipboard" size={20} />
          <IbTypography.Paragraph strong>{translate('ELMA365: Fetch ELMA365 task tool: Title')}</IbTypography.Paragraph>
        </div>
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.create_elma365_task_tool_settings.enabled}
            size="small"
            onChange={onCreateTaskEnabledChange}
          />
          <IbIcon iconName="clipboard" size={20} />
          <IbTypography.Paragraph strong>
            {translate('ELMA365: Create ELMA365 task tool: Title')}
          </IbTypography.Paragraph>
        </div>
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.reassign_elma365_task_tool_settings.enabled}
            size="small"
            onChange={onReassignTaskEnabledChange}
          />
          <IbIcon iconName="clipboard" size={20} />
          <IbTypography.Paragraph strong>
            {translate('ELMA365: Reassign ELMA365 task tool: Title')}
          </IbTypography.Paragraph>
        </div>
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.update_elma365_task_percent_tool_settings.enabled}
            size="small"
            onChange={onUpdateTaskPercentEnabledChange}
          />
          <IbIcon iconName="clipboard" size={20} />
          <IbTypography.Paragraph strong>
            {translate('ELMA365: Update ELMA365 task percent tool: Title')}
          </IbTypography.Paragraph>
        </div>
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.update_elma365_task_dates_tool_settings.enabled}
            size="small"
            onChange={onUpdateTaskDatesEnabledChange}
          />
          <IbIcon iconName="clipboard" size={20} />
          <IbTypography.Paragraph strong>
            {translate('ELMA365: Update ELMA365 task dates tool: Title')}
          </IbTypography.Paragraph>
        </div>
      </div>
    </div>
  );

  const renderDocflowTools = () => (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={elma365ToolsetSettings.fetch_docflow_lists_tool_settings.enabled}
            size="small"
            onChange={onFetchDocflowListsEnabledChange}
          />
          <IbIcon iconName="view-list" size={20} />
          <IbTypography.Paragraph strong>
            {translate('ELMA365: Fetch docflow lists tool: Title')}
          </IbTypography.Paragraph>
        </div>
      </div>
    </div>
  );

  const renderTabTitle = (title: string, toolCount: number) => (
    <>
      {title}
      {toolCount > 0 && <IbBadge value={toolCount} />}
    </>
  );

  const renderTabs = () => (
    <IbTabs activeKey={tabKey} className={TABS_CLASS_NAME} onChange={onTabChange}>
      <IbTabPane
        key={Elma365GeneralParametersTabKey.Applications}
        tab={renderTabTitle(translate('ELMA365: Tabs: Applications'), appToolCount)}
        tabKey={Elma365GeneralParametersTabKey.Applications}
      >
        {renderAppTools()}
      </IbTabPane>
      <IbTabPane
        key={Elma365GeneralParametersTabKey.Files}
        tab={renderTabTitle(translate('ELMA365: Tabs: Files'), fileToolCount)}
        tabKey={Elma365GeneralParametersTabKey.Files}
      >
        {renderFileTools()}
      </IbTabPane>
      <IbTabPane
        key={Elma365GeneralParametersTabKey.Users}
        tab={renderTabTitle(translate('ELMA365: Tabs: Users'), userToolCount)}
        tabKey={Elma365GeneralParametersTabKey.Users}
      >
        {renderUserTools()}
      </IbTabPane>
      <IbTabPane
        key={Elma365GeneralParametersTabKey.Tasks}
        tab={renderTabTitle(translate('ELMA365: Tabs: Tasks'), taskToolCount)}
        tabKey={Elma365GeneralParametersTabKey.Tasks}
      >
        {renderTaskTools()}
      </IbTabPane>
      <IbTabPane
        key={Elma365GeneralParametersTabKey.Docflow}
        tab={renderTabTitle(translate('ELMA365: Tabs: Docflow'), docflowToolCount)}
        tabKey={Elma365GeneralParametersTabKey.Docflow}
      >
        {renderDocflowTools()}
      </IbTabPane>
    </IbTabs>
  );

  return (
    <div className={PARAMETERS_CLASS_NAME}>
      {renderConnectorSettings()}
      {renderToolNamesPrefixSettings()}
      {renderTabs()}
    </div>
  );
};

export default Elma365GeneralParameters;
